import {Subject, interval, type Subscription, merge} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import moment, {type Moment} from 'moment-timezone';
import {Device, type DeviceInfo} from '@capacitor/device';

import store from 'web-app/react/redux/store';
import * as AccountDetailsApi from 'web-app/react/account-settings/account-details/api';
import FamlyticsBehaviors from 'web-app/behaviors/famlytics-behavior-ids';
import * as SentryService from 'web-app/services/sentry';
import {partition} from 'web-app/util/array';
import {analyticsBehavior} from 'web-app/react/session/account/selectors';
import type BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import LocalStorageService from 'web-app/services/local-storage';
import PlatformHelper from 'web-app/helpers/platform-helper';
import LocalStorage from 'web-app/services/local-storage';
import {exhaustiveCheck} from 'web-app/util/typescript';
import {type FamlyticsEvent} from '@famly/stat_ts-utils_famlytics';
import {HEARTBEAT_KEY, OVERRIDDEN_REFRESH_INTERVAL_KEY} from '@famly/stat_ts-utils_famlytics';

import * as Api from './api';
import * as Storage from './storage';

type SelectedDeviceInfo = Pick<DeviceInfo, 'manufacturer' | 'platform' | 'osVersion'> & {model: string};

const getDeviceInfo = async (): Promise<SelectedDeviceInfo | undefined> => {
    if (!PlatformHelper.isMobileApp()) {
        return Promise.resolve(undefined);
    }

    const deviceInfo = await Device.getInfo();

    return {
        manufacturer: deviceInfo.manufacturer,
        model: deviceInfo.model,
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
    };
};

const heartbeat = () => {
    const registerHeartbeat = (time: Moment) => {
        LocalStorageService.saveToLocalStorage(HEARTBEAT_KEY, time);
    };

    const now = moment();

    const lastSavedHeartbeat = LocalStorageService.getFromLocalStorage(HEARTBEAT_KEY);

    if (!lastSavedHeartbeat) {
        registerHeartbeat(now);
        return;
    }

    registerHeartbeat(now);
};

interface AnalyticsEnabler {
    token: string;
    expires: string;
}

const getToken = (): Promise<AnalyticsEnabler> => {
    const behavior = analyticsBehavior(store.getState());

    if (!behavior) {
        return Promise.reject(Error('No famlytics behavior present'));
    }

    const {token, expires} = behavior.payload;

    if (moment(expires).diff(moment(), 'hours') <= 1) {
        return AccountDetailsApi.fetchAccount().then((account: any) => {
            const behaviors: BehaviorsCollection = account.behaviors;
            const newBehavior = behaviors.behaviorForId(FamlyticsBehaviors.EnableAnalytics);

            if (!newBehavior) {
                return Promise.reject(Error('No famlytics behavior present'));
            }

            return Promise.resolve({
                token: newBehavior.payload.token,
                expires: newBehavior.payload.expires,
            });
        });
    } else {
        return Promise.resolve({token, expires});
    }
};

const chunkSize = 50;

const overriddenRefreshInterval = Number(LocalStorage.getFromLocalStorage(OVERRIDDEN_REFRESH_INTERVAL_KEY));

const refreshInterval = overriddenRefreshInterval || 45000; // ms
const backoffMultiplier = 1.5;
const initialOptions = {
    factor: 1,
    retries: 0,
};
const maxRetries = 5;
const intervalSubject = new Subject<typeof initialOptions>();
const manualSubject = new Subject<{type: 'manual'}>();

export const triggerManuallyAndFlush = () => {
    manualSubject.next({type: 'manual'});
};

const buildAndStartConsumer = async () => {
    heartbeat();

    const deviceInfo = await getDeviceInfo();

    const intervalObservable = intervalSubject.pipe(
        switchMap(options => {
            // eslint-disable-next-line
            return interval(refreshInterval * options.factor).pipe(map(_ => ({type: 'interval' as const, options})));
        }),
    );

    const manualObservable = manualSubject.asObservable();

    // We have two "triggers" in place here
    // One that triggers at an interval with a backup
    // And one that is manually triggered
    const subscription = merge(intervalObservable, manualObservable)
        .pipe(
            tap(payload => {
                const sendEvents = () => {
                    const account = store.getState().session.account;
                    const loginId = account.loginId;
                    const localSecret = account.localSecret;

                    if (!loginId || !localSecret) {
                        return;
                    }

                    const dequeuedStoredEvents = Storage.peek(chunkSize, localSecret, loginId);

                    if (dequeuedStoredEvents.length <= 0) {
                        return;
                    }

                    // Find and remove events that did not decrypt properly.
                    const {passed: storedEvents, failed: decryptionFailures} = partition(
                        dequeuedStoredEvents,
                        (event): event is {id: string; event: FamlyticsEvent} => event.event !== undefined,
                    );

                    const decryptionFailureIds = decryptionFailures.map(event => event.id);

                    Storage.remove(decryptionFailureIds, loginId);

                    const eventsOnly = storedEvents.map(({event}) => {
                        return {
                            ...event,
                            meta: {
                                ...event.meta,
                                device: deviceInfo,
                            },
                        };
                    });

                    if (eventsOnly.length <= 0) {
                        return;
                    }

                    getToken()
                        .then(({token, expires}) => {
                            return Api.track(eventsOnly, token, expires);
                        })
                        .then((result: any) => {
                            switch (payload.type) {
                                case 'manual':
                                    // When doing the manual trigger, we want to flush all events
                                    Storage.flush();
                                    break;
                                case 'interval':
                                    Storage.remove(result.received, loginId);
                                    break;
                                default:
                                    exhaustiveCheck(payload);
                            }
                        })
                        .catch(e => {
                            // If the format of the payload is wrong we'll probably not be able to recover.
                            // Flush the wrong events.
                            if (e && e.status === 400) {
                                Storage.remove(
                                    dequeuedStoredEvents.map(event => event.id),
                                    loginId,
                                );
                                SentryService.captureMessage('Incorrect formats for famlytics events', {});
                            }

                            if (payload.type === 'interval') {
                                // Back off when we encounter any error.
                                intervalSubject.next({
                                    factor: payload.options.factor * backoffMultiplier,
                                    retries: payload.options.retries + 1,
                                });
                            }
                        });
                };

                switch (payload.type) {
                    case 'manual':
                        sendEvents();
                        return;
                    case 'interval': {
                        const {retries} = payload.options;

                        heartbeat();

                        if (retries >= maxRetries) {
                            Storage.removeAll();
                            return;
                        }

                        sendEvents();
                        return;
                    }
                    default:
                        exhaustiveCheck(payload);
                        return;
                }
            }),
        )
        .subscribe();

    intervalSubject.next(initialOptions);

    return subscription;
};

let subscriptionSingleton: Subscription;

export const getConsumer = async () => {
    if (subscriptionSingleton) {
        return subscriptionSingleton;
    } else {
        subscriptionSingleton = await buildAndStartConsumer();
        return subscriptionSingleton;
    }
};
